import { z } from "zod";
import { ufSchema } from "./calculation.js";
import { certificationLevelSchema } from "./fiscal-facts.js";
import { ruleProposalDraftSchema, ruleProposalDraftStatusSchema } from "./rule-drafts.js";
/**
 * NF-e observed tax paths (Regras → Verificação → Notas fiscais).
 *
 * An operator uploads authorised NF-e XML files (layout 4.00, `nfeProc` or a
 * bare `NFe`, one by one or inside a `.zip`). Each item becomes an
 * observation of how a real supplier taxed a real operation; items sharing a
 * route, NCM (+ CEST), CFOP, operation type and full tax treatment form one
 * observed path.
 *
 * CORROBORATION ONLY. An invoice is not a legal source: an observed path
 * never certifies a fiscal fact, never changes a rule and never publishes.
 * Comparison with the platform is read-only. A draft proposal created from a
 * path still needs official evidence attached and approved in Documentos.
 *
 * Data minimisation: the raw XML, names, addresses, e-mails and phones are
 * never stored. The destination CNPJ is kept only when it is the platform's
 * own company. Rates in `treatment` keep the NF-e unit (percent, `18` =
 * 18 %); every comparison value is a decimal fraction like the rules
 * (`0.18`).
 */
/* -- Limits ------------------------------------------------------------------ */
/**
 * Decoded bytes accepted in one upload request (all files together). Sized
 * for the admin service (1 GiB, 60 s per request): a larger zip is split by
 * the web client into several requests (or by the caller).
 */
export const NFE_IMPORT_MAX_REQUEST_BYTES = 8 * 1024 * 1024;
/** Top-level files in one upload request. */
export const NFE_IMPORT_MAX_FILES = 500;
/** One XML document (loose or inside a zip), uncompressed. */
export const NFE_IMPORT_MAX_XML_BYTES = 3 * 1024 * 1024;
/** Entries a zip may hold (directories and skipped entries count). */
export const NFE_IMPORT_MAX_ZIP_ENTRIES = 5_000;
/** Uncompressed bytes a zip may expand to, all entries together. */
export const NFE_IMPORT_MAX_ZIP_UNCOMPRESSED_BYTES = 128 * 1024 * 1024;
/** Uncompressed ÷ compressed size above which an entry is treated as a zip bomb. */
export const NFE_IMPORT_MAX_COMPRESSION_RATIO = 100;
/** NF-e documents examined in one request (zip entries and loose files). */
export const NFE_IMPORT_MAX_DOCUMENTS = 3_000;
/** Items accepted in one request; documents beyond it are TOO_MANY_DOCUMENTS. */
export const NFE_IMPORT_MAX_ITEMS = 60_000;
/** NF-e layout 4.00 allows at most 990 `det` items per invoice. */
export const NFE_IMPORT_MAX_ITEMS_PER_DOCUMENT = 990;
/** Base64 length of NFE_IMPORT_MAX_REQUEST_BYTES (the request body cap is derived from it). */
export const NFE_IMPORT_MAX_BASE64_LENGTH = Math.ceil(NFE_IMPORT_MAX_REQUEST_BYTES / 3) * 4;
/* -- Primitives -------------------------------------------------------------- */
/** Canonical padded base64, checked without a backtracking regex (payloads reach ~11 MB). */
export function isCanonicalBase64(value) {
    if (value.length % 4 !== 0)
        return false;
    const padding = value.endsWith("==") ? 2 : value.endsWith("=") ? 1 : 0;
    return /^[A-Za-z0-9+/]*$/.test(value.slice(0, value.length - padding));
}
const ncmSchema = z.string().regex(/^\d{8}$/);
const ncmPrefixSchema = z.string().regex(/^\d{2,8}$/);
const cestSchema = z.string().regex(/^\d{2}\.\d{3}\.\d{2}$/);
const cfopSchema = z.string().regex(/^[1-7]\d{3}$/);
const cnpjSchema = z.string().regex(/^\d{14}$/);
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
/** Decimal fraction like the rules (0.18 = 18 %). */
const rateSchema = z.string().regex(/^(0|[1-9]\d{0,5})(\.\d{1,8})?$/);
/** NF-e percent, trailing zeros removed (`18.0000` → `18`, `71.7800` → `71.78`). */
export const nfePercentSchema = z.string().regex(/^(0|[1-9]\d{0,3})(\.\d{1,4})?$/);
/** NF-e money (TDec_1302), trailing zeros kept to two places (`16.37`). */
export const nfeMoneySchema = z.string().regex(/^(0|[1-9]\d{0,12})\.\d{2}$/);
const digitSchema = z.string().regex(/^\d$/);
/** 44-digit access key (chave de acesso): cUF, AAMM, CNPJ, mod, série, nNF, tpEmis, cNF, cDV. */
export const nfeAccessKeySchema = z.string().regex(/^\d{44}$/);
/** Mod-11 check digit of the first 43 digits (Manual de Orientação do Contribuinte). */
export function nfeAccessKeyCheckDigit(first43) {
    let sum = 0;
    let weight = 2;
    for (let index = first43.length - 1; index >= 0; index -= 1) {
        sum += Number(first43[index]) * weight;
        weight = weight === 9 ? 2 : weight + 1;
    }
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
}
/** Format and check digit. Says nothing about authorisation at SEFAZ. */
export function isValidNfeAccessKey(value) {
    if (!/^\d{44}$/.test(value))
        return false;
    return nfeAccessKeyCheckDigit(value.slice(0, 43)) === Number(value[43]);
}
/** `3526 0312 3456 …`: groups of four digits, as printed on the DANFE. */
export function formatNfeAccessKey(value) {
    return value.replace(/(\d{4})(?=\d)/g, "$1 ");
}
/* -- Tax treatment ----------------------------------------------------------- */
/** ICMS group element of the item (`ICMS00`, `ICMS10`, `ICMSSN201`, `ICMSPart`, `ICMSST`, …). */
export const nfeIcmsGroupSchema = z.string().regex(/^ICMS(\d{2}|Part|ST|SN\d{3})$/);
export const nfeIcmsTreatmentSchema = z
    .object({
    group: nfeIcmsGroupSchema,
    /** Regime normal (CRT 3). */
    cst: z
        .string()
        .regex(/^\d{2}$/)
        .nullable(),
    /** Simples Nacional (CRT 1/2/4). */
    csosn: z
        .string()
        .regex(/^\d{3}$/)
        .nullable(),
    modBC: digitSchema.nullable(),
    pRedBC: nfePercentSchema.nullable(),
    pICMS: nfePercentSchema.nullable(),
    pFCP: nfePercentSchema.nullable(),
    modBCST: digitSchema.nullable(),
    pMVAST: nfePercentSchema.nullable(),
    pRedBCST: nfePercentSchema.nullable(),
    pICMSST: nfePercentSchema.nullable(),
    pFCPST: nfePercentSchema.nullable(),
    /** ICMS60/ICMSSN500: rate the final consumer bears on the retained ST (pST). */
    pST: nfePercentSchema.nullable(),
    pFCPSTRet: nfePercentSchema.nullable(),
})
    .strict();
/** ICMSUFDest (DIFAL, EC 87/2015) rates. */
export const nfeDifalTreatmentSchema = z
    .object({
    pICMSUFDest: nfePercentSchema.nullable(),
    pICMSInter: nfePercentSchema.nullable(),
    pFCPUFDest: nfePercentSchema.nullable(),
})
    .strict();
export const nfeTreatmentSchema = z
    .object({
    /** Goods origin (0 national … 8); 1, 2, 3 and 8 take 4 % interstate (Res. Senado 13/2012). */
    orig: z.string().regex(/^[0-8]$/),
    /** Código de benefício fiscal (cBenef), when stated. */
    cBenef: z
        .string()
        .regex(/^[A-Z0-9]{8,10}$/)
        .nullable(),
    icms: nfeIcmsTreatmentSchema,
    difal: nfeDifalTreatmentSchema.nullable(),
    ipi: z
        .object({
        cst: z.string().regex(/^\d{2}$/),
        pIPI: nfePercentSchema.nullable(),
        cEnq: z
            .string()
            .regex(/^\d{3}$/)
            .nullable(),
    })
        .strict()
        .nullable(),
    pis: z
        .object({ cst: z.string().regex(/^\d{2}$/), rate: nfePercentSchema.nullable() })
        .strict()
        .nullable(),
    cofins: z
        .object({ cst: z.string().regex(/^\d{2}$/), rate: nfePercentSchema.nullable() })
        .strict()
        .nullable(),
    /** IBS/CBS (LC 214/2025) as stated on the invoice. Observed only; never compared. */
    ibsCbs: z
        .object({
        cst: z.string().regex(/^\d{3}$/),
        cClassTrib: z
            .string()
            .regex(/^\d{6}$/)
            .nullable(),
        pIBSUF: nfePercentSchema.nullable(),
        pIBSMun: nfePercentSchema.nullable(),
        pCBS: nfePercentSchema.nullable(),
    })
        .strict()
        .nullable(),
})
    .strict();
/** Item bases and values kept to recompute and check consistency. */
export const nfeItemValuesSchema = z
    .object({
    vProd: nfeMoneySchema,
    vFrete: nfeMoneySchema.nullable(),
    vSeg: nfeMoneySchema.nullable(),
    vDesc: nfeMoneySchema.nullable(),
    vOutro: nfeMoneySchema.nullable(),
    vBC: nfeMoneySchema.nullable(),
    vICMS: nfeMoneySchema.nullable(),
    vFCP: nfeMoneySchema.nullable(),
    vBCST: nfeMoneySchema.nullable(),
    vICMSST: nfeMoneySchema.nullable(),
    vBCFCPST: nfeMoneySchema.nullable(),
    vFCPST: nfeMoneySchema.nullable(),
    vBCSTRet: nfeMoneySchema.nullable(),
    vICMSSTRet: nfeMoneySchema.nullable(),
    vBCUFDest: nfeMoneySchema.nullable(),
    vICMSUFDest: nfeMoneySchema.nullable(),
    vFCPUFDest: nfeMoneySchema.nullable(),
    ipiVBC: nfeMoneySchema.nullable(),
    vIPI: nfeMoneySchema.nullable(),
    pisVBC: nfeMoneySchema.nullable(),
    vPIS: nfeMoneySchema.nullable(),
    cofinsVBC: nfeMoneySchema.nullable(),
    vCOFINS: nfeMoneySchema.nullable(),
    ibsCbsVBC: nfeMoneySchema.nullable(),
    vIBSUF: nfeMoneySchema.nullable(),
    vIBSMun: nfeMoneySchema.nullable(),
    vCBS: nfeMoneySchema.nullable(),
})
    .strict();
/** Internal consistency checks on one item (value ≠ base × rate beyond rounding). */
export const nfeItemIssueSchema = z.enum([
    "ICMS_VALUE_MISMATCH",
    "FCP_VALUE_MISMATCH",
    "ICMS_ST_BASE_MISMATCH",
    "ICMS_ST_VALUE_MISMATCH",
    "FCP_ST_VALUE_MISMATCH",
    "ST_RETAINED_VALUE_MISMATCH",
    "DIFAL_VALUE_MISMATCH",
    "IPI_VALUE_MISMATCH",
    "PIS_VALUE_MISMATCH",
    "COFINS_VALUE_MISMATCH",
    "IBS_CBS_VALUE_MISMATCH",
]);
/* -- Operation --------------------------------------------------------------- */
export const nfeOperationSchema = z
    .object({
    /** idDest 1 → INTERNAL, 2 → INTERSTATE (idDest 3, foreign trade, is not imported). */
    scope: z.enum(["INTERNAL", "INTERSTATE"]),
    /** 0 entrada, 1 saída. */
    tpNF: z.enum(["0", "1"]),
    /** 1 normal, 2 complementar, 3 ajuste, 4 devolução. */
    finNFe: z.enum(["1", "2", "3", "4"]),
    /** indFinal 1: final consumer. */
    finalConsumer: z.boolean(),
    /** indIEDest 1 contribuinte, 2 isento, 9 não contribuinte. */
    recipient: z.enum(["CONTRIBUTOR", "EXEMPT_CONTRIBUTOR", "NON_CONTRIBUTOR"]),
})
    .strict();
/* -- Comparison -------------------------------------------------------------- */
export const nfePathStatusSchema = z.enum([
    /** Every field the platform states agrees with the invoices. */
    "MATCHES",
    /** At least one stated field disagrees (see `divergences`). */
    "DIVERGES",
    /** Neither a published rule nor a CERTIFIED/VERIFIED fact covers the route + NCM. */
    "NOT_COVERED",
]);
export const NFE_PATH_STATUSES = nfePathStatusSchema.options;
export const nfeComparisonFieldSchema = z.enum([
    "stApplicable",
    /** pMVAST: the original MVA internally, the original or adjusted MVA interstate. */
    "mva",
    /** pICMSST (ST), pICMSUFDest (DIFAL) or pICMS on an internal operation. */
    "destinationInternalRate",
    /** pICMS (or pICMSInter) on an interstate operation. */
    "interstateRate",
    /** pFCPST, pFCPUFDest or pFCP, compared with the rule's FCP when it states one. */
    "fcpRate",
]);
export const nfeExpectationSourceSchema = z.enum([
    "PUBLISHED_RULE",
    /** CERTIFIED/VERIFIED fact from web research or an operator. */
    "FISCAL_FACT",
    /** CERTIFIED fact the IOB simulator answered (NO_ST_FOUND, INTERNAL_ST_ONLY, ST_FOUND). */
    "IOB_FINDING",
    /** Imported goods (orig 1, 2, 3, 8): 4 % interstate. */
    "SENATE_RESOLUTION_13_2012",
]);
const comparisonValueSchema = z.union([z.boolean(), rateSchema]);
export const nfeExpectedFieldSchema = z
    .object({
    value: comparisonValueSchema,
    source: nfeExpectationSourceSchema,
    /** Fiscal rule id or fact id behind the value. */
    ref: z.string().min(1).max(100).nullable(),
    /** Interstate MVA ajustada derived from the original MVA and the two rates. */
    adjusted: rateSchema.optional(),
})
    .strict();
export const nfeComparisonNoteSchema = z.enum([
    /** CST 60 / CSOSN 500: ST was retained earlier; ST yes/no is not compared interstate. */
    "ST_PREVIOUSLY_RETAINED",
    /** Final consumer: ST and MVA are not compared (ST concerns the resale chain). */
    "FINAL_CONSUMER",
    /** modBCST ≠ 4: the ST base is not an MVA (pauta, list price): MVA not compared. */
    "ST_BASE_NOT_MVA",
    /** CSOSN: the own ICMS rate is not stated on the item. */
    "SIMPLES_NACIONAL",
    /** orig 1, 2, 3 or 8: the 4 % interstate rate applies. */
    "IMPORTED_GOODS",
    /** The IOB answered INTERNAL_ST_ONLY: no retention by the sender, ST inside the destination. */
    "INTERNAL_ST_ONLY",
    /** The observed MVA matched the adjusted MVA, not the original one. */
    "ADJUSTED_MVA",
    /** The published rule and the certified facts disagree; the rule was used. */
    "RULE_FACT_DISAGREE",
    /** Entry invoice, complementary, adjustment or return: not an ordinary sale. */
    "NOT_ORDINARY_SALE",
    /** pRedBC / pRedBCST present: the nominal rates are compared. */
    "REDUCED_BASE",
    /** Some items failed the internal consistency checks. */
    "INCONSISTENT_ITEMS",
    /** Certified facts are ambiguous or conflicting for this key. */
    "FACTS_UNRESOLVED",
]);
export const nfeObservedValuesSchema = z
    .object({
    /** null when not comparable (previously retained interstate, final consumer). */
    stApplicable: z.boolean().nullable(),
    stMode: z.enum(["RETAINED", "PREVIOUSLY_RETAINED", "NONE"]),
    mva: rateSchema.nullable(),
    destinationInternalRate: rateSchema.nullable(),
    interstateRate: rateSchema.nullable(),
    fcpRate: rateSchema.nullable(),
})
    .strict();
export const nfeDivergenceSchema = z
    .object({
    field: nfeComparisonFieldSchema,
    expected: comparisonValueSchema,
    observed: comparisonValueSchema.nullable(),
    source: nfeExpectationSourceSchema,
    ref: z.string().min(1).max(100).nullable(),
})
    .strict();
export const nfePathComparisonSchema = z
    .object({
    status: nfePathStatusSchema,
    /** Date the platform state was read for (the path's last issue date, Brasília). */
    comparedOn: z.string().date(),
    publishedRule: z
        .object({
        rulesetVersion: z.string().min(1),
        policyVersion: z.string().min(1),
        fiscalRuleId: z.string().min(1),
    })
        .strict()
        .nullable(),
    /** Weakest level of the facts used (NONE when no fact answered). */
    factLevel: certificationLevelSchema,
    factIds: z.array(z.string().uuid()).max(4),
    expected: z
        .object({
        stApplicable: nfeExpectedFieldSchema.optional(),
        mva: nfeExpectedFieldSchema.optional(),
        destinationInternalRate: nfeExpectedFieldSchema.optional(),
        interstateRate: nfeExpectedFieldSchema.optional(),
        fcpRate: nfeExpectedFieldSchema.optional(),
    })
        .strict(),
    observed: nfeObservedValuesSchema,
    divergences: z.array(nfeDivergenceSchema).max(5),
    notes: z.array(nfeComparisonNoteSchema).max(12),
})
    .strict();
/* -- Observed paths ---------------------------------------------------------- */
export const nfeObservedPathSchema = z
    .object({
    id: z.string().uuid(),
    /** sha256 of the canonical path key (route, NCM, CEST, CFOP, operation, treatment). */
    pathKey: sha256Schema,
    originUf: ufSchema,
    destinationUf: ufSchema,
    ncm: ncmSchema,
    cest: cestSchema.nullable(),
    cfop: cfopSchema,
    operation: nfeOperationSchema,
    treatment: nfeTreatmentSchema,
    counts: z
        .object({
        items: z.number().int().positive(),
        invoices: z.number().int().positive(),
        /** Distinct emitter CNPJs (CPF emitters are not stored, so not counted). */
        emitters: z.number().int().nonnegative(),
        inconsistentItems: z.number().int().nonnegative(),
    })
        .strict(),
    issueCounts: z.partialRecord(nfeItemIssueSchema, z.number().int().positive()),
    firstIssuedAt: z.string().datetime({ offset: true }),
    lastIssuedAt: z.string().datetime({ offset: true }),
    comparison: nfePathComparisonSchema,
    updatedAt: z.string().datetime(),
})
    .strict();
/** `GET /v1/nfe-observed-paths` filters (READ:TAX_RULE:ALL). */
export const nfeObservedPathQuerySchema = z
    .object({
    originUf: ufSchema.optional(),
    destinationUf: ufSchema.optional(),
    ncmPrefix: ncmPrefixSchema.optional(),
    statuses: z.array(nfePathStatusSchema).min(1).max(3).optional(),
})
    .strict();
export const nfeObservedPathPageSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    items: z.array(nfeObservedPathSchema).max(200),
    nextCursor: z.string().min(1).nullable(),
    hasMore: z.boolean(),
    totalEstimate: z.number().int().nonnegative(),
    /** Paths per status under the UF/NCM filters (the status filter aside). */
    statusCounts: z
        .object({
        MATCHES: z.number().int().nonnegative(),
        DIVERGES: z.number().int().nonnegative(),
        NOT_COVERED: z.number().int().nonnegative(),
    })
        .strict(),
    /** True when more paths matched than one comparison scan reads (narrow the filters). */
    truncated: z.boolean(),
})
    .strict();
export const NFE_PATH_SAMPLE_INVOICES = 20;
export const nfeSampleInvoiceSchema = z
    .object({
    accessKey: nfeAccessKeySchema,
    issuedAt: z.string().datetime({ offset: true }),
    /** Null when the emitter identified itself with a CPF (never stored). */
    emitterCnpj: cnpjSchema.nullable(),
})
    .strict();
export const nfeInconsistentItemSchema = z
    .object({
    accessKey: nfeAccessKeySchema,
    itemNumber: z.number().int().min(1).max(990),
    issues: z.array(nfeItemIssueSchema).min(1).max(11),
    values: nfeItemValuesSchema,
})
    .strict();
/** `GET /v1/nfe-observed-paths/{pathId}` (READ:TAX_RULE:ALL). */
export const nfeObservedPathDetailSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    path: nfeObservedPathSchema,
    /** Newest first: access key, issue date and emitter CNPJ only. */
    sampleInvoices: z.array(nfeSampleInvoiceSchema).max(NFE_PATH_SAMPLE_INVOICES),
    inconsistentItems: z.array(nfeInconsistentItemSchema).max(NFE_PATH_SAMPLE_INVOICES),
    /** Proposal drafts already created from this path. */
    drafts: z
        .array(z
        .object({
        id: z.string().uuid(),
        status: ruleProposalDraftStatusSchema,
        targetRulesetVersion: z.string().min(1),
        proposalId: z.string().uuid().nullable(),
    })
        .strict())
        .max(20),
})
    .strict();
/**
 * `POST /v1/nfe-observed-paths/{pathId}/rule-proposal-draft`
 * (CREATE:TAX_RULE:ALL, Idempotency-Key). Only DIVERGES or NOT_COVERED
 * paths. Creates (or returns) one EVIDENCE_REQUIRED proposal draft in the
 * current DRAFT ruleset with the observation as NFE_OBSERVATION
 * corroboration. Never approves or publishes.
 */
export const nfePathDraftRequestSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    reason: z.string().trim().min(10).max(2_000).optional(),
})
    .strict();
export const nfePathDraftResultSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    /** False when a draft for this path already existed in the draft ruleset. */
    created: z.boolean(),
    draft: ruleProposalDraftSchema,
})
    .strict();
/* -- Imports ----------------------------------------------------------------- */
export const nfeImportFileSchema = z
    .object({
    filename: z
        .string()
        .min(1)
        .max(255)
        .regex(/\.(xml|zip)$/i),
    contentBase64: z
        .string()
        .min(4)
        .max(NFE_IMPORT_MAX_BASE64_LENGTH)
        // A grouped, repeated regex overflows V8's stack on multi-MB payloads;
        // a flat character class plus a length check is linear.
        .refine(isCanonicalBase64, "contentBase64 must be canonical base64"),
})
    .strict();
/** `POST /v1/nfe-imports` (CREATE:TAX_NEWS:ALL, Idempotency-Key). */
export const nfeImportRequestSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    files: z.array(nfeImportFileSchema).min(1).max(NFE_IMPORT_MAX_FILES),
})
    .strict();
export const nfeImportOutcomeSchema = z.enum([
    "IMPORTED",
    /** The access key was already imported (earlier upload or earlier in this one). */
    "DUPLICATE",
    /** Not a usable NF-e: see `reason`. */
    "INVALID",
    /** Not an NF-e candidate at all (DANFE PDF, folder, macOS metadata, CT-e, inutilização): ignored. */
    "SKIPPED",
    /**
     * A cancellation: the invoice's protocol says cancelled (cStat 101/135/155),
     * a cancellation event (procEventoNFe 110111) was recorded, or the invoice
     * arrived after its event. Cancelled invoices never enter an observed path.
     */
    "CANCELLED",
]);
export const nfeImportReasonSchema = z.enum([
    // DUPLICATE
    "ALREADY_IMPORTED",
    "REPEATED_IN_UPLOAD",
    // IMPORTED
    /** The original authorised invoice replaced an inverted ERP copy stored earlier. */
    "REPLACED_INVERTED_COPY",
    // CANCELLED
    /** The protocol (protNFe) carries cStat 101, 135 or 155. */
    "PROTOCOL_CANCELLED",
    /** A cancellation event (110111) was recorded, or already existed for this invoice. */
    "CANCELLATION_EVENT",
    // SKIPPED
    /** CT-e, MDF-e or inutilização XML: valid XML, not an NF-e. */
    "NOT_NFE_DOCUMENT",
    /** An NF-e event other than cancellation (CC-e, manifestação…). */
    "EVENT_IGNORED",
    "NOT_XML",
    "DIRECTORY",
    "SYSTEM_FILE",
    "NESTED_ZIP",
    "FOREIGN_OPERATION",
    // INVALID: file / zip
    "FILE_TOO_LARGE",
    "ZIP_INVALID",
    "ZIP_TOO_MANY_ENTRIES",
    "ZIP_TOO_LARGE",
    "ZIP_ENTRY_UNSAFE_PATH",
    "ZIP_ENTRY_ENCRYPTED",
    "ZIP_ENTRY_UNSUPPORTED",
    "ZIP_ENTRY_TOO_LARGE",
    "ZIP_ENTRY_RATIO",
    "ZIP_ENTRY_CORRUPT",
    /** Beyond the per-request document or item cap: upload it again in another request. */
    "TOO_MANY_DOCUMENTS",
    // INVALID (SKIPPED when the original is already on file): Omie "XML_Invertido" mirror
    "INVERTED_ERP_COPY",
    // INVALID: XML / NF-e
    "XML_MALFORMED",
    "XML_DTD_FORBIDDEN",
    "NOT_NFE",
    "UNSUPPORTED_VERSION",
    "UNSUPPORTED_MODEL",
    "INVALID_ACCESS_KEY",
    "ACCESS_KEY_MISMATCH",
    "NOT_AUTHORIZED",
    "HOMOLOGATION",
    "MISSING_FIELD",
    "INVALID_FIELD",
    "TOO_MANY_ITEMS",
]);
export const nfeImportFileResultSchema = z
    .object({
    seq: z.number().int().positive(),
    /** Uploaded file name, or the entry path inside `container`. */
    filename: z.string().min(1).max(255),
    container: z.string().min(1).max(255).nullable(),
    outcome: nfeImportOutcomeSchema,
    reason: nfeImportReasonSchema.nullable(),
    /** Field or element behind MISSING_FIELD / INVALID_FIELD (`ide/dhEmi`). */
    detail: z.string().max(120).nullable(),
    accessKey: nfeAccessKeySchema.nullable(),
    sha256: sha256Schema.nullable(),
    sizeBytes: z.number().int().nonnegative(),
    itemCount: z.number().int().nonnegative(),
})
    .strict();
export const nfeImportCountsSchema = z
    .object({
    imported: z.number().int().nonnegative(),
    duplicate: z.number().int().nonnegative(),
    invalid: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    /** Invoices (or events) that cancel an invoice: excluded from paths and comparison. */
    cancelled: z.number().int().nonnegative(),
})
    .strict();
export const nfeImportBatchSchema = z
    .object({
    id: z.string().uuid(),
    /** COMPLETED: nothing invalid; COMPLETED_WITH_ERRORS: some entries were invalid. */
    status: z.enum(["COMPLETED", "COMPLETED_WITH_ERRORS"]),
    fileCount: z.number().int().positive(),
    entryCount: z.number().int().nonnegative(),
    totalBytes: z.number().int().nonnegative(),
    counts: nfeImportCountsSchema,
    itemCount: z.number().int().nonnegative(),
    pathCount: z.number().int().nonnegative(),
    createdBy: z.string().uuid(),
    createdAt: z.string().datetime(),
    requestId: z.string().min(1).max(200),
    correlationId: z.string().min(1).max(200),
})
    .strict();
export const NFE_IMPORT_MAX_RESULTS = NFE_IMPORT_MAX_DOCUMENTS + NFE_IMPORT_MAX_FILES;
/** `YYYY-MM`, the month of `dhEmi` in America/Sao_Paulo (tax manager workspace). */
export const nfePeriodSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);
/** A workspace month an upload touched. */
export const nfeAffectedPeriodSchema = z
    .object({
    period: nfePeriodSchema,
    /** The month was VALIDATED and went back to IN_REVIEW. */
    reopened: z.boolean(),
    changedInvoices: z.number().int().positive(),
})
    .strict();
/** Response of the upload and of `GET /v1/nfe-imports/{batchId}`. */
export const nfeImportBatchDetailSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    batch: nfeImportBatchSchema,
    files: z.array(nfeImportFileResultSchema).max(NFE_IMPORT_MAX_RESULTS),
    /** Months the upload changed, with the review state it left them in (upload response only). */
    affectedPeriods: z.array(nfeAffectedPeriodSchema).max(240).optional(),
})
    .strict();
export const nfeImportBatchPageSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    items: z.array(nfeImportBatchSchema).max(200),
    nextCursor: z.string().min(1).nullable(),
    hasMore: z.boolean(),
    totalEstimate: z.number().int().nonnegative(),
})
    .strict();
//# sourceMappingURL=nfe-observations.js.map